import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Video, Mic, MicOff, VideoOff, Phone, Users, MessageSquare, Calendar, Clock } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_talentmeet-app/artifacts/w1exeb5m_IMG-20260127-WA0008.jpg";

// Sample image for video call mockup
const SAMPLE_AGENT = "https://images.unsplash.com/photo-1616835240434-d91feb116120?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxhY3RvciUyMGhlYWRzaG90JTIwcHJvZmVzc2lvbmFsJTIwc3R1ZGlvJTIwbGlnaHRpbmd8ZW58MHx8fHwxNzY5ODU3NDAyfDA&ixlib=rb-4.1.0&q=85";

export default function MeetingRoom() {
  const navigate = useNavigate();
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [activeTab, setActiveTab] = useState("meeting"); // meeting, schedule

  const scheduledMeetings = [
    { id: 1, title: "Portfolio Review", with: "Sarah Johnson - Talent Agent", date: "Jan 30, 2024", time: "2:00 PM", status: "upcoming" },
    { id: 2, title: "Career Discussion", with: "Mike Thompson - Manager", date: "Feb 2, 2024", time: "11:00 AM", status: "scheduled" },
    { id: 3, title: "Contract Review", with: "Legal Team", date: "Feb 5, 2024", time: "3:30 PM", status: "scheduled" }
  ];

  if (activeTab === "schedule") {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-white border-b border-gray-100 px-4 py-3">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <img src={LOGO_URL} alt="TalentMeet" className="h-8 w-8 rounded-lg" />
                <span className="font-display text-xl font-bold text-gray-900">Meeting Room</span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                variant={activeTab === "meeting" ? "default" : "outline"}
                onClick={() => setActiveTab("meeting")}
                data-testid="meeting-tab"
              >
                <Video className="w-4 h-4 mr-2" /> Join Meeting
              </Button>
              <Button 
                variant={activeTab === "schedule" ? "default" : "outline"}
                onClick={() => setActiveTab("schedule")}
                data-testid="schedule-tab"
              >
                <Calendar className="w-4 h-4 mr-2" /> Schedule
              </Button>
            </div>
          </div>
        </header>

        {/* Scheduled Meetings */}
        <main className="max-w-4xl mx-auto px-4 py-8">
          <h2 className="font-display text-2xl font-bold text-gray-900 mb-6">Scheduled Meetings</h2>
          <div className="space-y-4">
            {scheduledMeetings.map(meeting => (
              <Card key={meeting.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{meeting.title}</h3>
                      <p className="text-gray-600 text-sm">{meeting.with}</p>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" /> {meeting.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" /> {meeting.time}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {meeting.status === "upcoming" && (
                        <Badge className="bg-teal-100 text-teal-800">Starting Soon</Badge>
                      )}
                      <Button 
                        onClick={() => setActiveTab("meeting")}
                        className={meeting.status === "upcoming" ? "btn-primary" : ""}
                        variant={meeting.status === "upcoming" ? "default" : "outline"}
                      >
                        {meeting.status === "upcoming" ? "Join Now" : "View Details"}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="mt-8 border-violet-200 bg-violet-50">
            <CardContent className="p-6 text-center">
              <Calendar className="w-12 h-12 text-violet-600 mx-auto mb-4" />
              <h3 className="font-display text-lg font-semibold text-gray-900 mb-2">Schedule a New Meeting</h3>
              <p className="text-gray-600 text-sm mb-4">Connect with agents, managers, and industry professionals.</p>
              <Button className="btn-primary" data-testid="schedule-meeting-btn">
                Schedule Meeting
              </Button>
              <p className="mt-4 text-xs text-gray-500">
                <Badge variant="outline">Demo Mode</Badge> Scheduling feature coming soon
              </p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-white hover:bg-gray-700">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="TalentMeet" className="h-8 w-8 rounded-lg" />
              <span className="font-display text-xl font-bold text-white">Meeting Room</span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              variant={activeTab === "meeting" ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveTab("meeting")}
              className="text-white"
            >
              <Video className="w-4 h-4 mr-2" /> Meeting
            </Button>
            <Button 
              variant={activeTab === "schedule" ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveTab("schedule")}
            >
              <Calendar className="w-4 h-4 mr-2" /> Schedule
            </Button>
          </div>
        </div>
      </header>

      {/* Main Video Area */}
      <main className="max-w-7xl mx-auto p-4">
        <div className="grid lg:grid-cols-4 gap-4">
          {/* Main Video */}
          <div className="lg:col-span-3">
            <Card className="bg-gray-800 border-gray-700 overflow-hidden">
              <CardContent className="p-0 relative">
                <div className="relative aspect-video bg-gray-900">
                  <img 
                    src={SAMPLE_AGENT}
                    alt="Agent"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-4 left-4 bg-black/50 backdrop-blur-sm rounded-lg px-3 py-1">
                    <span className="text-white text-sm font-medium">Sarah Johnson - Talent Agent</span>
                  </div>
                  
                  {/* Self View */}
                  <div className="absolute bottom-4 right-4 w-48 h-36 bg-gray-800 rounded-lg overflow-hidden border-2 border-gray-600">
                    {isVideoOn ? (
                      <div className="w-full h-full bg-gradient-to-br from-teal-600 to-violet-600 flex items-center justify-center">
                        <span className="text-white text-3xl font-bold">You</span>
                      </div>
                    ) : (
                      <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                        <VideoOff className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Controls */}
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                  <div className="flex items-center justify-center gap-4">
                    <Button
                      variant={isMuted ? "destructive" : "secondary"}
                      size="lg"
                      className="rounded-full w-14 h-14"
                      onClick={() => setIsMuted(!isMuted)}
                    >
                      {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
                    </Button>
                    <Button
                      variant={isVideoOn ? "secondary" : "destructive"}
                      size="lg"
                      className="rounded-full w-14 h-14"
                      onClick={() => setIsVideoOn(!isVideoOn)}
                    >
                      {isVideoOn ? <Video className="w-6 h-6" /> : <VideoOff className="w-6 h-6" />}
                    </Button>
                    <Button
                      variant="destructive"
                      size="lg"
                      className="rounded-full w-14 h-14"
                      onClick={() => navigate("/talent/dashboard")}
                    >
                      <Phone className="w-6 h-6 rotate-[135deg]" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Meeting Info */}
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-4">
                <Badge className="bg-teal-500 text-white mb-3">
                  <span className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse"></span>
                  Active Meeting
                </Badge>
                <h3 className="text-white font-semibold mb-2">Portfolio Review</h3>
                <p className="text-gray-400 text-sm">with Sarah Johnson</p>
                <p className="text-gray-500 text-xs mt-2">Duration: 45 minutes</p>
              </CardContent>
            </Card>

            {/* Participants */}
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-4">
                <h3 className="text-white font-semibold flex items-center gap-2 mb-4">
                  <Users className="w-5 h-5" /> Participants (2)
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-pink-500 flex items-center justify-center">
                      <span className="text-white font-bold">SJ</span>
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">Sarah Johnson</p>
                      <p className="text-gray-400 text-xs">Talent Agent</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-teal-500 flex items-center justify-center">
                      <span className="text-white font-bold">Y</span>
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">You</p>
                      <p className="text-gray-400 text-xs">Talent</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Chat */}
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-4">
                <h3 className="text-white font-semibold flex items-center gap-2 mb-4">
                  <MessageSquare className="w-5 h-5" /> Chat
                </h3>
                <div className="h-32 flex items-center justify-center text-gray-500 text-sm">
                  <p>No messages yet</p>
                </div>
                <Badge className="mt-3 bg-violet-500/20 text-violet-300 border border-violet-500/30">
                  Demo Mode - Mocked UI
                </Badge>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
